import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { useTechnologiesDictionary } from "../../hooks/queries/useTechnologiesDictionary";
import {
  TechnologyCategory,
  TechnologyCategoryMapping,
} from "../../models/technology-category.model";
import { TechnologyDictionaryModel } from "../../models/technology.model";
import { TechnologyCategoryService } from "../../services/technologyCategoryService";
import { TechnologyService } from "../../services/technologyService";

export function TechnologyCategoriesManagement() {
  const [activeTab, setActiveTab] = useState<string | null>("categories");
  const [categories, setCategories] = useState<TechnologyCategory[]>([]);
  const [mappings, setMappings] = useState<TechnologyCategoryMapping[]>([]);
  const { data: technologies, isLoading: technologiesLoading } =
    useTechnologiesDictionary();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchCategory, setSearchCategory] = useState("");
  const [searchTechnology, setSearchTechnology] = useState("");
  const [editedCategoryId, setEditedCategoryId] = useState<number | null>(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTechnology, setSelectedTechnology] = useState<string | null>(
    null
  );
  const [opened, { open, close }] = useDisclosure(false);
  const [modalType, setModalType] = useState<"edit" | "add" | "remove">("edit");
  const [actionData, setActionData] = useState<any>(null);

  const categoryService = useMemo(() => new TechnologyCategoryService(), []);
  const technologyService = useMemo(() => new TechnologyService(), []);

  // Function to convert the dictionary data to mappings
  const convertTechnologiesToMappings = (
    technologies: TechnologyDictionaryModel[]
  ): TechnologyCategoryMapping[] => {
    const mappings: TechnologyCategoryMapping[] = [];

    technologies.forEach((tech) => {
      if (tech.categories && tech.categories.length > 0) {
        tech.categories.forEach((category) => {
          mappings.push({
            technologyId: tech.id,
            technologyName: tech.name,
            categoryId: category.id,
            categoryName: category.name,
          });
        });
      }
    });

    return mappings;
  };

  const loadData = async () => {
    if (!technologies) return;

    setIsLoading(true);
    setError(null);
    try {
      const categoriesData = await categoryService.getCategories();
      setCategories(categoriesData);

      // Convert technology data to mappings (technologies come from React Query)
      const mappingsData = convertTechnologiesToMappings(technologies || []);
      setMappings(mappingsData);
    } catch (err) {
      console.error("Error loading technology categories data:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load data. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (technologies && !technologiesLoading) {
      loadData();
    }
  }, [technologies, technologiesLoading]);

  const handleOpenEditModal = (category: TechnologyCategory) => {
    setEditedCategoryId(category.id);
    setEditedCategoryName(category.name);
    setModalType("edit");
    open();
  };

  const handleOpenAddMappingModal = () => {
    setModalType("add");
    open();
  };

  const handleOpenRemoveMappingModal = (mapping: TechnologyCategoryMapping) => {
    setModalType("remove");
    setActionData(mapping);
    open();
  };

  const handleSaveCategory = async () => {
    if (!editedCategoryName.trim()) return;

    try {
      if (editedCategoryId) {
        await categoryService.updateCategoryName(
          editedCategoryId,
          editedCategoryName
        );
        notifications.show({
          title: "Success",
          message: "Category updated successfully",
          color: "green",
        });
      }

      setEditedCategoryId(null);
      setEditedCategoryName("");
      close();
      await loadData();
    } catch (err) {
      console.error("Error saving category:", err);
      notifications.show({
        title: "Error",
        message: "Failed to save category",
        color: "red",
      });
    }
  };

  const handleAddMapping = async () => {
    if (!selectedCategory || !selectedTechnology) return;

    try {
      await categoryService.addTechnologyToCategory(
        parseInt(selectedTechnology),
        parseInt(selectedCategory)
      );
      notifications.show({
        title: "Success",
        message: "Technology added to category successfully",
        color: "green",
      });

      setSelectedCategory(null);
      setSelectedTechnology(null);
      close();
      await loadData();
    } catch (err) {
      console.error("Error adding mapping:", err);
      notifications.show({
        title: "Error",
        message: "Failed to add technology to category",
        color: "red",
      });
    }
  };

  const handleRemoveMapping = async () => {
    if (!actionData) return;

    try {
      await categoryService.removeTechnologyFromCategory(
        actionData.technologyId,
        actionData.categoryId
      );
      notifications.show({
        title: "Success",
        message: "Technology removed from category successfully",
        color: "green",
      });

      setActionData(null);
      close();
      await loadData();
    } catch (err) {
      console.error("Error removing mapping:", err);
      notifications.show({
        title: "Error",
        message: "Failed to remove technology from category",
        color: "red",
      });
    }
  };

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  const filteredMappings = mappings.filter(
    (mapping) =>
      mapping.technologyName
        .toLowerCase()
        .includes(searchTechnology.toLowerCase()) ||
      mapping.categoryName
        .toLowerCase()
        .includes(searchTechnology.toLowerCase())
  );

  const categoryOptions = categories.map((cat) => ({
    value: cat.id.toString(),
    label: cat.name,
  }));

  const technologyOptions = (technologies || []).map((tech) => ({
    value: tech.id.toString(),
    label: tech.name,
  }));

  const renderModalContent = () => {
    switch (modalType) {
      case "edit":
        return (
          <>
            <TextInput
              label="Category Name"
              value={editedCategoryName}
              onChange={(e) => setEditedCategoryName(e.currentTarget.value)}
              placeholder="Enter category name"
              required
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                disabled={!editedCategoryName.trim()}
              >
                Save
              </Button>
            </Group>
          </>
        );
      case "add":
        return (
          <>
            <Select
              label="Skill or Technology"
              placeholder="Select a technology"
              data={technologyOptions}
              value={selectedTechnology}
              onChange={setSelectedTechnology}
              searchable
              required
            />
            <Select
              label="Category"
              placeholder="Select a category"
              data={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
              searchable
              required
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={handleAddMapping}
                disabled={!selectedCategory || !selectedTechnology}
              >
                Add
              </Button>
            </Group>
          </>
        );
      case "remove":
        return actionData ? (
          <>
            <Text mb="md">
              Are you sure you want to remove <b>{actionData.technologyName}</b>{" "}
              from the <b>{actionData.categoryName}</b> category?
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button color="red" onClick={handleRemoveMapping}>
                Remove
              </Button>
            </Group>
          </>
        ) : null;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error}
        <Button onClick={loadData} mt="md" variant="outline" size="sm">
          Try Again
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Categories Section */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600} size="lg">
              Categories
            </Text>
            <Button
              leftSection={<ArrowPathIcon style={{ width: rem(16) }} />}
              onClick={loadData}
              variant="default"
              size="sm"
            >
              Refresh
            </Button>
          </Group>

          <TextInput
            placeholder="Search categories..."
            value={searchCategory}
            onChange={(e) => setSearchCategory(e.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon style={{ width: rem(16) }} />}
          />

          <ScrollArea>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Skills & Technologies</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredCategories.map((category) => (
                  <Table.Tr key={category.id}>
                    <Table.Td>{category.name}</Table.Td>
                    <Table.Td>
                      <Badge variant="outline">
                        {
                          mappings.filter((m) => m.categoryId === category.id)
                            .length
                        }{" "}
                        items
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Edit Category">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="blue"
                            onClick={() => handleOpenEditModal(category)}
                          >
                            <PencilIcon style={{ width: rem(16) }} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Stack>
      </Paper>

      {/* Mappings Section */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600} size="lg">
              Technology-Category Mappings
            </Text>
            <Button
              leftSection={<PlusIcon style={{ width: rem(16) }} />}
              onClick={handleOpenAddMappingModal}
              size="sm"
            >
              Add Mapping
            </Button>
          </Group>

          <TextInput
            placeholder="Search mappings..."
            value={searchTechnology}
            onChange={(e) => setSearchTechnology(e.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon style={{ width: rem(16) }} />}
          />

          <ScrollArea>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Skill or Technology</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredMappings.map((mapping, index) => (
                  <Table.Tr
                    key={`${mapping.technologyId}-${mapping.categoryId}`}
                  >
                    <Table.Td>{mapping.technologyName}</Table.Td>
                    <Table.Td>
                      <Badge color="blue" variant="light">
                        {mapping.categoryName}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Remove from Category">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => handleOpenRemoveMappingModal(mapping)}
                        >
                          <XMarkIcon style={{ width: rem(16) }} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Stack>
      </Paper>

      <Modal
        opened={opened}
        onClose={close}
        title={
          modalType === "edit"
            ? "Edit Category"
            : modalType === "add"
              ? "Add Technology to Category"
              : "Remove Technology from Category"
        }
      >
        {renderModalContent()}
      </Modal>
    </Stack>
  );
}
